"use client";

import { useAppNavigation } from "@/components/use-app-navigation";
import { findGroupsByBaseUrl } from "@/utils/utils";
import { usePathname } from "next/navigation";
import { useSidebar } from "@/components/ui/sidebar";
import { Menu } from "lucide-react";
import { NavUser } from "@/components/nav-user";

type MobileSidebarToggleProps = {
  layoutName: string;
};

export function MobileSidebarToggle({ layoutName }: MobileSidebarToggleProps) {
  const { toggleSidebar, isMobile, openMobile } = useSidebar();
  const pathname = usePathname();
  const { data } = useAppNavigation();
  const groups = findGroupsByBaseUrl(data, pathname).filter(
    (group) => group.title !== "Resources" && group.pages.length > 0,
  );
  const currentPage = groups
    .flatMap((group) => group.pages)
    .find((page) =>
      page.exactMatch
        ? pathname === page.url
        : pathname === page.url || pathname.startsWith(`${page.url}/`),
    );

  if (!isMobile) return null;

  return (
    <header className="relative z-30 shrink-0 bg-sidebar text-sidebar-foreground">
      <div className="flex min-h-14 items-center gap-2 border-b px-3 py-2 sm:px-4">
        <button
          type="button"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          onClick={toggleSidebar}
          aria-expanded={openMobile}
          aria-haspopup="dialog"
        >
          <Menu aria-hidden="true" className="size-[22px]" />
          <span className="sr-only">Toggle menu</span>
        </button>
        <span
          aria-hidden="true"
          className="mr-[7px] h-4 w-px shrink-0 bg-border opacity-80"
        />
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 text-sm">
          <span className="font-semibold text-foreground">{layoutName}</span>
          {currentPage && (
            <span className="min-w-0 text-muted">
              <span aria-hidden="true" className="mr-1.5">
                /
              </span>
              <span aria-current="page">{currentPage.title}</span>
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center">
          <NavUser placement="header" />
        </div>
      </div>
    </header>
  );
}
